import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { useGameDataOverview, type LeagueName } from './api';
import { DataTable, DataTableShell, formatTimestamp } from './shared';

export function GameDataOverviewTab({ league }: { league: LeagueName }) {
  const { data, isLoading, error } = useGameDataOverview(league);
  const dataset = data?.data;

  if (isLoading || error || !dataset) {
    return (
      <DataTableShell loading={isLoading} error={error} empty={!dataset}>
        <div />
      </DataTableShell>
    );
  }

  // Structural counts only (the league's "shape" — not user-state).
  // `rounds` is the registered scope_code count (EPL: 38, WC: 8), sourced
  // from the game-modes registry on the backend rather than counting
  // Round rows.
  const metrics = [
    ['Rounds', dataset.rounds],
    ['Fixtures', dataset.fixtures],
    ['Teams', dataset.teams],
    ['Players', dataset.players],
    ['Coaches', dataset.coaches],
  ] as const;

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">{dataset.display_name}</CardTitle>
          <Badge variant={league === 'EPL' ? 'default' : 'outline'}>{dataset.league_name}</Badge>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid gap-3 md:grid-cols-4">
            <Field label="Sport" value={dataset.sport} />
            <Field label="Season" value={dataset.season} />
            <Field label="Provider" value={dataset.provider} />
            <Field label="Scope" value={dataset.scope_label} />
          </div>
          <Field
            label="Latest fixture"
            value={
              dataset.latest_fixture
                ? `${formatTimestamp(dataset.latest_fixture.kickoff)} · ${dataset.latest_fixture.status}`
                : 'No fixtures ingested'
            }
          />
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        {metrics.map(([label, value]) => (
          <Card key={label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">
                {label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-medium tabular-nums">{value.toLocaleString()}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <DataTable
        rows={dataset.mappings.map((mapping) => ({ mapping }))}
        rowKey={(row) => row.mapping}
        columns={[{ key: 'mapping', label: 'Persisted mapping', render: (row) => row.mapping }]}
      />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-medium">{value}</div>
    </div>
  );
}
